import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { BarChart3, ClipboardEdit, History, LogOut, Menu, User, LayoutDashboard, Users, Target, FileText, Store, ScrollText, Settings, Undo2, Coins, PackageOpen } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Facet } from "./kit";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";

const STAFF_NAV = [
  { to: "/me", label: "Performance", icon: BarChart3 },
  { to: "/entry", label: "Entry", icon: ClipboardEdit },
  { to: "/gms", label: "GMS", icon: Coins },
  { to: "/returns", label: "Returns", icon: Undo2 },
  { to: "/history", label: "History", icon: History },
  { to: "/account", label: "Profile", icon: User },
];

const MGMT_NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/team", label: "Team", icon: Users },
  { to: "/gms", label: "GMS", icon: Coins },
  { to: "/mto", label: "MTO", icon: PackageOpen },
  { to: "/returns", label: "Returns", icon: Undo2 },
  { to: "/targets", label: "Targets", icon: Target },
  { to: "/reports", label: "Reports", icon: FileText },
  { to: "/stores", label: "Stores", icon: Store, admin: true },
  { to: "/audit", label: "Audit", icon: ScrollText, admin: true },
  { to: "/settings", label: "Settings", icon: Settings, admin: true },
];

export const Brand = ({ light = true }) => (
  <div className="flex items-center gap-2.5" data-testid="brand-logo">
    <Facet className={`w-6 h-6 ${light ? "text-[#C5A059]" : "text-[#011E38]"}`} />
    <div className="leading-none">
      <div className={`font-serif-accent text-2xl font-semibold tracking-wide ${light ? "text-white" : "text-[#011E38]"}`}>BlueStone</div>
      <div className={`text-[0.6rem] tracking-[0.28em] uppercase ${light ? "text-white/50" : "text-slate-500"}`}>Retail Performance</div>
    </div>
  </div>
);

function StoreSelector() {
  const { user, stores, storeId, setStoreId } = useAuth();
  const navigate = useNavigate();
  if (user.role === "staff" || (user.role === "manager" && stores.length < 2)) {
    const s = stores.find((x) => x.id === storeId);
    return s ? <span className="hidden sm:inline text-sm text-white/70" data-testid="current-store-name">{s.name}</span> : null;
  }
  return (
    <Select value={storeId || undefined} onValueChange={(v) => { setStoreId(v); navigate("/dashboard"); }}>
      <SelectTrigger data-testid="store-selector" className="w-[200px] bg-white/10 border-white/20 text-white h-10">
        <SelectValue placeholder="Select store" />
      </SelectTrigger>
      <SelectContent>
        {user.role === "admin" && <SelectItem value="all" data-testid="store-option-all">All Stores</SelectItem>}
        {stores.map((s) => (
          <SelectItem key={s.id} value={s.id} data-testid={`store-option-${s.id}`}>{s.name}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

const linkCls = ({ isActive }) =>
  `flex items-center gap-2 px-3.5 py-2 rounded-full text-sm font-medium transition-colors ${isActive ? "bg-white text-[#011E38]" : "text-white/75 hover:text-white hover:bg-white/10"}`;

function MgmtLinks({ onClick }) {
  const { user } = useAuth();
  return MGMT_NAV.filter((n) => !n.admin || user.role === "admin").map((n) => (
    <NavLink key={n.to} to={n.to} className={linkCls} onClick={onClick} data-testid={`nav-${n.label.toLowerCase()}`}>
      <n.icon className="w-4 h-4" /> {n.label}
    </NavLink>
  ));
}

export default function Layout({ children }) {
  const { user, month, setMonth, logout, basis, setBasis, setRange } = useAuth();
  const pickMonth = (m) => {
    const [y, mo] = m.split("-").map(Number);
    setMonth(m);
    setRange({ preset: "custom", start: `${m}-01`, end: `${m}-${String(new Date(y, mo, 0).getDate()).padStart(2, "0")}` });
  };
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const isStaff = user.role === "staff";
  const doLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <header className="sticky top-0 z-40 bg-[#011E38]/95 backdrop-blur-md shadow-lg">
        <div className={`${isStaff ? "max-w-md" : "max-w-7xl"} mx-auto px-4 sm:px-6 h-16 flex items-center gap-4`}>
          {!isStaff && (
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger className="lg:hidden text-white" data-testid="mobile-menu-button"><Menu /></SheetTrigger>
              <SheetContent side="left" className="bs-navy-panel border-0 w-72">
                <div className="mb-8"><Brand /></div>
                <nav className="flex flex-col gap-1"><MgmtLinks onClick={() => setOpen(false)} /></nav>
              </SheetContent>
            </Sheet>
          )}
          <NavLink to="/"><Brand /></NavLink>
          {!isStaff && <nav className="hidden lg:flex items-center gap-1 ml-4"><MgmtLinks /></nav>}
          <div className="ml-auto flex items-center gap-3">
            {!isStaff && <StoreSelector />}
            {!isStaff && month && (
              <input
                type="month" value={month} onChange={(e) => e.target.value && pickMonth(e.target.value)}
                data-testid="month-picker"
                className="hidden md:block h-10 rounded-md bg-white/10 border border-white/20 text-white px-2 text-sm [color-scheme:dark]"
              />
            )}
            {!isStaff && (
              <NavLink to="/account" className="text-white/80 hover:text-white" data-testid="nav-account"><User className="w-5 h-5" /></NavLink>
            )}
            <button onClick={doLogout} className="text-white/80 hover:text-white" data-testid="logout-button" title="Sign out">
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>
      <main className={isStaff ? "max-w-md mx-auto w-full px-4 pt-6 pb-28" : "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10"}>
        {children}
      </main>
      {isStaff && (
        <nav className="fixed bottom-0 inset-x-0 z-40 bg-white border-t border-slate-200 pb-[env(safe-area-inset-bottom)]">
          <div className="max-w-md mx-auto grid grid-cols-6">
            {STAFF_NAV.map((n) => (
              <NavLink key={n.to} to={n.to} data-testid={`staff-nav-${n.label.toLowerCase()}`}
                className={({ isActive }) => `flex flex-col items-center gap-1 py-3 text-xs font-semibold ${isActive ? "text-[#011E38]" : "text-slate-400"}`}>
                <n.icon className="w-5 h-5" /> {n.label}
              </NavLink>
            ))}
          </div>
        </nav>
      )}
    </div>
  );
}
